import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import axios from "axios";
import { API_URL } from "./config";
import { motion } from "framer-motion";
import { Business, HighlightOff, WarningAmber } from "@mui/icons-material";
import { AdminPageShell, AdminStat, AdminSurface } from "./admin/AdminPageShell";

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '18px',
        bgcolor: '#fff',
    },
};

const DeactivateBusiness = () => {
    const [businesses, setBusinesses] = useState([]);
    const [formData, setFormData] = useState({ business: "" });
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(API_URL + "deactivate.php", { withCredentials: true });
                if (response.data.success) {
                    setBusinesses(response.data.businesses);
                } else {
                    setErrorMessage(response.data.message || "Failed to fetch data.");
                }
            } catch (error) {
                setErrorMessage("Error fetching data. Please try again.");
            }
        };

        fetchData();
    }, []);

    const selectedBusiness = useMemo(
        () => businesses.find((business) => business.id === formData.business),
        [businesses, formData.business],
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setSuccessMessage("");
        setErrorMessage("");

        try {
            const response = await axios.post(API_URL + "deactivate.php", formData, {
                withCredentials: true,
            });

            if (response.data.success) {
                setSuccessMessage(response.data.message);
                setFormData({ business: "" });
            } else {
                setErrorMessage(response.data.message);
            }
        } catch (error) {
            setErrorMessage("Error Deactivating Business. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminPageShell
            icon={<HighlightOff />}
            eyebrow="Business lifecycle"
            title="Deactivate Business"
            description="Use this page to disable a business account with a clearer warning-first layout that matches the rest of the admin experience."
            maxWidth="lg"
        >
            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(240px, 320px) minmax(0, 1fr)" },
                    alignItems: "start",
                    width: "100%",
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Stack spacing={2}>
                        <AdminStat label="Businesses loaded" value={businesses.length} helper="These businesses can be selected for deactivation." />
                        <AdminSurface sx={{ borderColor: 'rgba(255, 152, 0, 0.24)', background: 'rgba(255,248,238,0.92)' }}>
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                <WarningAmber sx={{ color: 'warning.main', mt: 0.25 }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#5d3d00', mb: 0.75 }}>
                                        Action warning
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(93,61,0,0.78)' }}>
                                        Deactivation impacts business access immediately. Double-check the selected business before confirming.
                                    </Typography>
                                </Box>
                            </Stack>
                        </AdminSurface>
                    </Stack>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <AdminSurface>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, color: '#2d2a5a' }}>
                                    Select business to deactivate
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                                    Choose the business carefully, review the summary, and then confirm the action.
                                </Typography>
                            </Box>

                            <Box component="form" onSubmit={handleSubmit}>
                                <TextField
                                    label="Select business"
                                    name="business"
                                    value={formData.business}
                                    onChange={handleInputChange}
                                    select
                                    fullWidth
                                    required
                                    sx={fieldStyles}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Business color="primary" />
                                            </InputAdornment>
                                        ),
                                    }}
                                >
                                    {businesses.map((business) => (
                                        <MenuItem key={business.id} value={business.id} sx={{ py: 1.5 }}>
                                            {business.name}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                <AdminSurface
                                    sx={{
                                        mt: 2.5,
                                        p: 2.5,
                                        borderColor: 'rgba(255, 152, 0, 0.2)',
                                        background: 'linear-gradient(180deg, rgba(255,248,238,0.96) 0%, rgba(255,255,255,0.96) 100%)',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                                        Selected business
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#2d2a5a' }}>
                                        {selectedBusiness?.name || 'No business selected yet'}
                                    </Typography>
                                </AdminSurface>

                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        disabled={loading}
                                        sx={{
                                            mt: 3,
                                            py: 1.6,
                                            borderRadius: '18px',
                                            fontWeight: 700,
                                            fontSize: '1rem',
                                            textTransform: 'none',
                                            background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
                                            boxShadow: '0 18px 30px rgba(239, 68, 68, 0.2)',
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Deactivate Business'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </Stack>
                    </AdminSurface>
                </Box>
            </Box>

            <Snackbar open={Boolean(successMessage)} autoHideDuration={4000} onClose={() => setSuccessMessage("") }>
                <Alert severity="success" onClose={() => setSuccessMessage("")} sx={{ width: '100%' }}>
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar open={Boolean(errorMessage)} autoHideDuration={4000} onClose={() => setErrorMessage("") }>
                <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </AdminPageShell>
    );
};

export default DeactivateBusiness;
